import * as Tabs from '@radix-ui/react-tabs';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useMemo, useEffect } from 'react';
import { plans } from '@/lib/constants';
import SearchBar from 'components/shared/SearchBar';
import { FiPlus } from 'react-icons/fi';
import FeaturesContent from './FeaturesContent';
import PlanModal from './PlanModal';
import PlansTable from './PlansTable';
import { getPlan, getPlans } from 'api/plans';

interface Plan {
    id: string;
    name: string;
    description: string;
    isActive: boolean;
    permissions?: string[];
    createdAt?: string;
}

interface PlansTabsProps {
    activeTab: string;
    onTabChange: (value: string) => void;
}

export default function PlansTabs({ activeTab, onTabChange }: PlansTabsProps) {
    const [plansData, setPlansData] = useState<Plan[]>([]);
    const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [mode, setMode] = useState<'create' | 'edit'>('edit');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const fetchPlans = async () => {
        setIsLoading(true);
        const response = await getPlans();
        if (response.status === 200) {
            const transformedPlans = response.data.list.map((plan: any) => ({
                id: plan.id,
                name: plan.name,
                description: plan.desc,
                isActive: plan.active,
                // permissions: plan.permissions,
            }));
            setPlansData(transformedPlans);
            setIsLoading(false);
        }else{
            setError(response.data.message);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
    }, []);

    // Add filtered roles using useMemo
    const filteredPlans = useMemo(() => {
        if (!searchTerm) return plansData;

        const searchLower = searchTerm.toLowerCase();
        return plansData.filter(plan =>
            plan.name.toLowerCase().includes(searchLower) ||
            plan.description.toLowerCase().includes(searchLower)
        );
    }, [plansData, searchTerm]);

    const handleEdit = async (plan: Plan) => {
        setMode('edit');
        const response = await getPlan(plan.id);
        const planData = response.data.data;
        if (response.status === 200) {
            setEditingPlan({
                id: planData.id,
                name: planData.name,
                description: planData.desc,
                isActive: planData.active,
                // permissions: response?.permissions,
                // createdAt: response?.createdAt
            });
        }

        setIsEditModalOpen(true);
    };

    const handleUpdatePlan = (updatedPlan: Plan) => {
        setPlansData(prevPlans =>
            prevPlans.map(plan =>
                plan.id === updatedPlan.id ? updatedPlan : plan
            )
        );
    };

    return (
        <div>
            <Tabs.Root value={activeTab} onValueChange={onTabChange}>
            <div className="sticky top-0 pt-4 bg-black z-20">
            <div className="flex justify-between items-center mb-4">
                    <h1 className="text-xl font-semibold text-white">Plans and Features</h1>
                </div>
                    <Tabs.List className="flex w-full">
                        <Tabs.Trigger
                            value="plans"
                            className={`px-6 py-2.5 text-sm transition-colors relative flex-1 max-w-[200px] ${
                                activeTab === 'plans'
                                    ? 'bg-gradient-to-r from-[#65f4cdcc] to-[#4799e9b3] text-white rounded-lg'
                                    : 'bg-[#2A2A2A] text-gray-400 rounded-l-lg'
                            }`}
                        >
                            Plans
                        </Tabs.Trigger>
                        <Tabs.Trigger
                            value="features"
                            className={`px-6 py-2.5 text-sm transition-colors relative flex-1 max-w-[200px] ${
                                activeTab === 'features'
                                    ? 'bg-gradient-to-r from-[#65f4cdcc] to-[#4799e9b3] text-white rounded-lg'
                                    : 'bg-[#2A2A2A] text-gray-400 rounded-r-lg'
                            }`}
                        >
                            Features
                        </Tabs.Trigger>
                        <div className="flex-1 bg-[#2A2A2A] h-full" />
                    </Tabs.List>
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.15 }}
                    >
                        <Tabs.Content value="plans">
                            {activeTab === 'plans' && (
                                <>
                                    <div className="sticky top-[100px] pt-4 bg-black z-10 ">
                                        <div className='flex justify-between items-center bg-black pb-4'>
                                            <SearchBar
                                                value={searchTerm}
                                                onChange={setSearchTerm}
                                                placeholder="Search plans..."
                                            />
                                            <button
                                                onClick={() => {
                                                    setMode('create');
                                                    setIsEditModalOpen(true);
                                                }}
                                                className="flex items-center gap-2 bg-[#2A2A2A] rounded-md px-4 py-2 text-white"
                                            >
                                                <FiPlus /> Add Plan
                                            </button>
                                        </div>

                                        <div className="bg-[#2A2A2A] rounded-t-lg">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="bg-[#1A1A1A] text-gray-400 text-sm">
                                                        {/* Your table headers */}
                                                    </tr>
                                                </thead>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            )}

                            {activeTab === 'plans' && (
                                <PlansTable
                                    plans={filteredPlans}
                                    onUpdatePlan={handleUpdatePlan}
                                    onEdit={handleEdit}
                                    fetchPlans={fetchPlans}
                                />
                            )}
                        </Tabs.Content>

                        <Tabs.Content value="features">
                            {activeTab === 'features' && <FeaturesContent />}
                        </Tabs.Content>
                    </motion.div>
                </AnimatePresence>
            </Tabs.Root>

            <PlanModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setEditingPlan(null);
                    setMode('create');
                }}
                mode={mode}
                initialData={editingPlan}
                fetchPlans={fetchPlans}
            />
        </div>
    );
} 